import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Audio } from 'expo-av';
import { getTranscription } from './apiService';

// Types
type StartFn = () => Promise<void> | void;
type StopFn = () => Promise<void> | void;

let startListening: StartFn = async () => {};
let stopListening: StopFn = async () => {};
let getTranscript: () => string = () => '';
let isListening = false;
let onTranscriptCallback: (t: string) => void = () => {};
let onErrorCallback: (e: any) => void = () => {};
let micPermissionGranted: boolean | null = null; // Cached in-memory flag

export function setTranscriptCallback(cb: (t: string) => void) {
  onTranscriptCallback = cb;
}
export function setErrorCallback(cb: (e: any) => void) {
  onErrorCallback = cb;
}
export function getCurrentTranscript() {
  return getTranscript();
}
export function getIsListening() {
  return isListening;
}

/* ---------- Microphone Permission Logic ---------- */
async function ensureMicPermission(): Promise<boolean> {
  // Fast in-memory check
  if (micPermissionGranted) return true;

  // Try AsyncStorage
  try {
    const stored = await AsyncStorage.getItem('micPermissionGranted');
    if (stored === 'true') {
      micPermissionGranted = true;
      return true;
    }
  } catch {}

  if (Platform.OS === 'web') {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
      micPermissionGranted = true;
      await AsyncStorage.setItem('micPermissionGranted', 'true');
      return true;
    } catch {
      onErrorCallback(new Error('Microphone permission denied'));
      return false;
    }
  } else {
    // Native (Expo)
    try {
      const { granted } = await Audio.requestPermissionsAsync();
      micPermissionGranted = granted;
      if (granted) await AsyncStorage.setItem('micPermissionGranted', 'true');
      else onErrorCallback(new Error('Microphone permission denied'));
      return granted;
    } catch (e) {
      onErrorCallback(e);
      return false;
    }
  }
}

/* ---------- Web implementation ---------- */
if (Platform.OS === 'web') {
  const Win = window as any;
  const NativeSpeech =
    Win.SpeechRecognition || Win.webkitSpeechRecognition || Win.mozSpeechRecognition || null;

  if (NativeSpeech) {
    // ✅ Use Web Speech API when available
    let transcript = '';
    const recog = new NativeSpeech();
    recog.continuous = true;
    recog.interimResults = true;
    recog.lang = 'en-US';

    recog.onresult = (ev: SpeechRecognitionEvent) => {
      let final = '';
      for (let i = 0; i < ev.results.length; ++i) {
        final += ev.results[i][0].transcript;
      }
      transcript = final;
      onTranscriptCallback(transcript);
    };

    recog.onerror = (e: any) => {
      onErrorCallback(e);
    };

    startListening = async () => {
      const allowed = await ensureMicPermission();
      if (!allowed) return;
      try {
        recog.start();
        isListening = true;
      } catch {}
    };

    stopListening = () => {
      try {
        recog.stop();
      } finally {
        isListening = false;
      }
    };

    getTranscript = () => transcript;
  } else {
    // Fallback: record → send blob → transcribe
    let transcript = '';
    let mediaStream: MediaStream | null = null;
    let mediaRecorder: MediaRecorder | null = null;
    const chunks: BlobPart[] = [];

    async function startRecorder() {
      const allowed = await ensureMicPermission();
      if (!allowed) return;

      try {
        mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        mediaRecorder = new MediaRecorder(mediaStream, { mimeType: 'audio/webm' });
        chunks.length = 0;

        mediaRecorder.ondataavailable = (e) => {
          if (e.data.size > 0) chunks.push(e.data);
        };
        mediaRecorder.start();
        isListening = true;
      } catch (e) {
        onErrorCallback(e);
      }
    }

    async function stopRecorderAndUpload() {
      try {
        if (!mediaRecorder) return;
        await new Promise<void>((res) => {
          mediaRecorder!.onstop = () => res();
          mediaRecorder!.stop();
        });

        isListening = false;
        const blob = new Blob(chunks, { type: 'audio/webm' });
        const transcriptResult = await getTranscription([blob]);
        transcript = transcriptResult;
        onTranscriptCallback(transcript);
      } catch (e) {
        onErrorCallback(e);
      } finally {
        mediaStream?.getTracks().forEach((t) => t.stop());
        mediaStream = null;
        mediaRecorder = null;
      }
    }

    startListening = startRecorder;
    stopListening = stopRecorderAndUpload;
    getTranscript = () => transcript;
  }
}

/* ---------- Expo / React Native implementation ---------- */
else {
  let recording: Audio.Recording | null = null;
  let transcript = '';

  async function startExpoRecording() {
    const allowed = await ensureMicPermission();
    if (!allowed) return;
    try {
      await Audio.setAudioModeAsync({
        allowsRecordingIOS: true,
        playsInSilentModeIOS: true,
      });

      const { recording: rec } = await Audio.Recording.createAsync(
        Audio.RecordingOptionsPresets.HIGH_QUALITY
      );

      recording = rec;
      isListening = true;
    } catch (e) {
      onErrorCallback(e);
    }
  }

  async function stopExpoRecording() {
    try {
      if (!recording) return;
      await recording.stopAndUnloadAsync();
      const uri = recording.getURI();
      if (!uri) throw new Error('No recording URI found');
      const transcriptResult = await getTranscription(uri);
      transcript = transcriptResult;
      onTranscriptCallback(transcript);
    } catch (e) {
      onErrorCallback(e);
    } finally {
      isListening = false;
      recording = null;
    }
  }

  startListening = startExpoRecording;
  stopListening = stopExpoRecording;
  getTranscript = () => transcript;
}

export { startListening, stopListening, getTranscript };
